import type { AxiosInstance } from "axios";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "../components/ui/badge.js";
import { Input } from "../components/ui/input.js";
import { cn } from "../lib/cn.js";
import { apiErrorMessage } from "../lib/api.js";
import { fetchAudit, type AuditRow } from "../lib/admin.js";

/** Full ISO timestamps carry their own offset, so a plain locale render is unambiguous. */
function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

/**
 * The complete cross-org audit — "who did what", the answer to "who moved report X out of
 * plan Y" and "who comped this org". Each row expands to the full before/after, the actor,
 * the IP and the exact resource, so an investigation never has to leave the page.
 */
export function AuditScreen({ client }: { readonly client: AxiosInstance }): React.JSX.Element {
  const [rows, setRows] = useState<readonly AuditRow[] | null>(null);
  const [action, setAction] = useState("");
  const [resourceId, setResourceId] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (actionFilter: string, resourceFilter: string) => {
      try {
        setRows(await fetchAudit(client, { action: actionFilter, resourceId: resourceFilter }));
        setError(null);
      } catch (caught) {
        setError(apiErrorMessage(caught, "Could not load the audit log."));
      }
    },
    [client],
  );

  useEffect(() => {
    const timer = setTimeout(() => void load(action, resourceId), 300);
    return () => clearTimeout(timer);
  }, [action, resourceId, load]);

  return (
    <div className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Audit</h1>
          <p className="text-muted-foreground text-sm">Every staff and system change, newest first. Click a row for the full change.</p>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Action, e.g. billing.subscription" className="h-9 w-64" value={action} onChange={(e) => setAction(e.target.value)} />
          <Input placeholder="Resource id" className="h-9 w-48" value={resourceId} onChange={(e) => setResourceId(e.target.value)} />
        </div>
      </header>

      {error !== null && <p className="text-destructive text-sm">{error}</p>}

      {rows === null ? (
        <p className="text-muted-foreground flex items-center gap-2 p-6 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </p>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground p-6 text-sm">No matching audit entries.</p>
      ) : (
        <div className="bg-card divide-y overflow-hidden rounded-lg border">
          {rows.map((row) => (
            <AuditEntry key={row.id} row={row} expanded={open === row.id} onToggle={() => setOpen(open === row.id ? null : row.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function AuditEntry({ row, expanded, onToggle }: { readonly row: AuditRow; readonly expanded: boolean; readonly onToggle: () => void }): React.JSX.Element {
  return (
    <div>
      <button type="button" onClick={onToggle} className="hover:bg-accent flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm">
        {expanded ? <ChevronDown className="size-4 shrink-0" /> : <ChevronRight className="text-muted-foreground size-4 shrink-0" />}
        <span className="text-muted-foreground w-40 shrink-0 text-xs">{when(row.createdAt)}</span>
        <span className="font-mono text-xs">{row.action}</span>
        <Badge variant="secondary" className="shrink-0">
          {row.actorType}
        </Badge>
        <span className="truncate text-xs">{row.actorEmail ?? "—"}</span>
        <span className="text-muted-foreground ml-auto truncate text-xs">
          {row.resourceType} · {row.resourceId.slice(0, 8)}
        </span>
      </button>

      {expanded && (
        <div className="bg-muted/30 grid gap-4 px-4 py-3 md:grid-cols-2">
          <Meta row={row} />
          <div className="grid gap-3">
            <JsonBlock label="Before" value={row.before} />
            <JsonBlock label="After" value={row.after} />
          </div>
        </div>
      )}
    </div>
  );
}

function Meta({ row }: { readonly row: AuditRow }): React.JSX.Element {
  const items: readonly [string, string][] = [
    ["Resource", `${row.resourceType} · ${row.resourceId}`],
    ["Organization", row.orgId ?? "—"],
    ["Actor", `${row.actorType}${row.actorEmail === null ? "" : ` · ${row.actorEmail}`}`],
    ["IP", row.ip ?? "—"],
    ["When", when(row.createdAt)],
  ];
  return (
    <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1.5 text-xs">
      {items.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="font-mono break-all">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function JsonBlock({ label, value }: { readonly label: string; readonly value: unknown }): React.JSX.Element {
  const empty = value === null || value === undefined;
  return (
    <div>
      <p className="text-muted-foreground mb-1 text-xs font-medium">{label}</p>
      <pre className={cn("overflow-x-auto rounded-md border p-2 text-xs", empty ? "text-muted-foreground" : "bg-card")}>
        {empty ? "—" : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
